import i18n, { supportedLangsLong } from "i18n";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { clearCurrentBoard } from "store/currentBoardSlice";
import { addToast } from "store/toastsSlice";
import {
  fetchUserProfile,
  selectUserInfo,
  updateUserLanguage,
} from "store/userSlice";

import Api from "api/Api";

import Button from "layout/atoms/Button";
import ContentBox from "layout/atoms/ContentBox";
import Form from "layout/atoms/Form";
import GridBoard from "layout/atoms/GridBoard";
import Header from "layout/atoms/Header";
import InputText from "layout/atoms/InputText";
import Select from "layout/atoms/Select";
import Separator from "layout/atoms/Separator";

function ProfileSettings() {
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const api = new Api();
  const { t } = useTranslation();

  // User language form
  const {
    register: registerUserLang,
    handleSubmit: handleSubmitUserLang,
    formState: formStateUserLang,
    formState: { errors: errorsUserLang, isDirty: isDirtyUserLang },
    reset: resetUserLang,
    setValue: setValueUserLang,
  } = useForm({
    defaultValues: {
      language: userInfo.lang,
    },
  });

  // Change password form
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: errorsPassword, isSubmitting: isSubmittingPassword },
    reset: resetPassword,
    setError: setErrorPassword,
    getValues: getValuesPassword,
  } = useForm();

  useEffect(() => {
    dispatch(clearCurrentBoard());
    dispatch(fetchUserProfile());
  }, [dispatch]);

  useEffect(() => {
    if (userInfo.lang) {
      resetUserLang({ language: userInfo.lang });
    }
  }, [userInfo.lang, resetUserLang]);

  // User language handlers
  const handleSaveUserLanguage = (data, e) => {
    if (data.language !== userInfo.lang) {
      // Change language immediately for instant feedback
      const langCode = data.language.split("-")[0];
      i18n.changeLanguage(langCode);

      dispatch(updateUserLanguage({ language: data.language }))
        .then(() => {
          dispatch(
            addToast({
              title: "toasts.dataSaved.title",
              description: "toasts.dataSaved.description",
              type: "success",
            }),
          );
          // Form will be reset automatically by the useEffect when userInfo.lang updates
        })
        .catch((error) => {
          dispatch(
            addToast({
              title: "toasts.error.title",
              description: "toasts.error.description",
              type: "error",
            }),
          );
        });
    }
  };

  const handleSaveUserLanguageError = (errors) => {
    dispatch(
      addToast({
        title: "toasts.error.title",
        description: "toasts.error.description",
        type: "error",
      }),
    );
  };

  const handleCancelUserLanguage = () => {
    resetUserLang({
      language: userInfo.lang,
    });
  };

  const hasUserLanguageChanges = isDirtyUserLang;

  // Change password handlers
  const handleCancelChangePassword = () => {
    resetPassword();
    setIsChangingPassword(false);
  };

  const handleChangePassword = (data) => {
    return api
      .changeUserPassword(data.currentPassword, data.newPassword)
      .then(() => {
        handleCancelChangePassword();
        dispatch(
          addToast({
            title: "toasts.passwordChanged.title",
            description: "toasts.passwordChanged.description",
            type: "success",
          }),
        );
      })
      .catch((error) => {
        // The backend answers 400 when the current password does not match
        if (error.message.startsWith("HTTP 400")) {
          setErrorPassword("currentPassword", {
            type: "incorrect",
            message: t("profileSettings.currentPasswordIncorrect"),
          });
          return;
        }
        dispatch(
          addToast({
            title: "toasts.error.title",
            description: "toasts.error.description",
            type: "error",
          }),
        );
      });
  };

  return (
    <GridBoard>
      {/* User Profile Section */}
      <ContentBox>
        <Header size="h3" margin="m">
          {t("profileSettings.yourProfile")}
        </Header>

        {/* Email Field (readonly) */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            {t("profileSettings.email")}
          </label>
          <input
            type="email"
            value={userInfo.email || ""}
            disabled
            readOnly
            style={{
              width: "100%",
              maxWidth: "400px",
              padding: "12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              backgroundColor: "#f5f5f5",
              color: "#666",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Language Section */}
        <div style={{ marginBottom: "20px", maxWidth: "400px" }}>
          <Select
            name="language"
            label={t("boardsDetails.forms.settings.fields.language.label")}
            register={registerUserLang}
            errors={errorsUserLang.language}
            options={supportedLangsLong}
          />
        </div>

        <Form
          onSubmit={handleSubmitUserLang(
            handleSaveUserLanguage,
            handleSaveUserLanguageError,
          )}
        >
          {hasUserLanguageChanges && (
            <div style={{ justifyContent: "flex-start" }}>
              <Separator />
              <Button
                type="button"
                size="lg"
                color="white"
                onClick={handleCancelUserLanguage}
              >
                {t("buttons.cancel")}
              </Button>
              <Button type="submit" size="lg" color="primary">
                {t("buttons.save")}
              </Button>
            </div>
          )}
        </Form>
      </ContentBox>

      {/* Password Section */}
      <ContentBox>
        <Header size="h3" margin="m">
          {t("profileSettings.password")}
        </Header>
        {isChangingPassword ? (
          <Form onSubmit={handleSubmitPassword(handleChangePassword)}>
            <div style={{ maxWidth: "400px" }}>
              <InputText
                type="password"
                name="currentPassword"
                label={t("profileSettings.currentPassword")}
                register={registerPassword}
                errors={errorsPassword.currentPassword}
                autoComplete="current-password"
                validation={{ required: true }}
              />
              <InputText
                type="password"
                name="newPassword"
                label={t("profileSettings.newPassword")}
                register={registerPassword}
                errors={errorsPassword.newPassword}
                autoComplete="new-password"
                validation={{
                  required: true,
                  minLength: 6,
                  maxLength: 50,
                }}
              />
              <InputText
                type="password"
                name="confirmNewPassword"
                label={t("profileSettings.confirmNewPassword")}
                register={registerPassword}
                errors={errorsPassword.confirmNewPassword}
                autoComplete="new-password"
                validation={{
                  required: true,
                  validate: (value) =>
                    value === getValuesPassword("newPassword") ||
                    t("profileSettings.passwordsDoNotMatch"),
                }}
              />
            </div>
            <div style={{ justifyContent: "flex-start" }}>
              <Separator />
              <Button
                type="button"
                size="lg"
                color="white"
                onClick={handleCancelChangePassword}
              >
                {t("buttons.cancel")}
              </Button>
              <Button
                type="submit"
                size="lg"
                color="primary"
                disabled={isSubmittingPassword}
              >
                {t("buttons.save")}
              </Button>
            </div>
          </Form>
        ) : (
          <>
            <div
              style={{
                maxWidth: "400px",
                marginBottom: "24px",
              }}
            >
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                {t("loginForm.passwordLabel")}
              </label>
              <input
                type="password"
                value="••••••••"
                disabled
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: "#f5f5f5",
                  color: "#666",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <Separator />
              <Button
                type="button"
                size="lg"
                color="primary"
                onClick={() => setIsChangingPassword(true)}
              >
                {t("profileSettings.changePassword")}
              </Button>
            </div>
          </>
        )}
      </ContentBox>
    </GridBoard>
  );
}

export default ProfileSettings;
